import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { EXAMPLES_FETCH_SIZE, EXAMPLES_MAX_ROWS, EXAMPLES_PAGE_SIZE } from "@nexus/contracts";
import {
  resolveDashboardRequestWindow,
  type DashboardRequestWindowInput,
} from "@/features/dashboard/lib/resolve-dashboard-request-window";
import type { TopicDetailContext } from "@/features/overview/lib/topicDetailContext.js";
import type { CompTopicDetailContext } from "@/features/overview/lib/compTopicDetailContext.js";
import { ApiError, authFetch } from "@/lib/api-client";
import { fetchAmberOptions } from "@/lib/queries/amber-options";
import type {
  OverviewFilters,
  OverviewReportRow,
  OverviewCompetitorReportRow,
  OverviewCompetitorChartRow,
  OverviewChartRow,
  OverviewTopicWeeklyRow,
  OverviewKpiSeries,
  TopicExampleRow,
  TopicFatiguePoint,
  CompTopicExampleRow,
  CompTopicFatiguePoint,
} from "@nexus/contracts";
import type { CompetitorFiltersInput, MyBrandFiltersInput } from "./dashboard.queries.js";

/** Absolute window carried on Overview requests — not part of the preset contract types. */
export type OverviewDateWindow = {
  startDate: string;
  endDate: string;
};

export type MyBrandOverviewFilters = MyBrandFiltersInput & Partial<OverviewDateWindow>;
export type CompetitorOverviewFilters = CompetitorFiltersInput & Partial<OverviewDateWindow>;

/**
 * Active explore mode owns its own window; the other mode's dates stay put.
 * Resolves catalog presets when store dates are empty (URL reload).
 */
export function pickOverviewRequestWindow(
  mode: "my-brand" | "competitors",
  myBrand: DashboardRequestWindowInput,
  competitor: DashboardRequestWindowInput,
  today?: Date,
): OverviewDateWindow {
  return resolveDashboardRequestWindow(
    mode === "competitors" ? competitor : myBrand,
    today,
  );
}

function appendDateWindow(
  qs: URLSearchParams,
  window: Partial<OverviewDateWindow>,
): void {
  if (window.startDate) qs.set("startDate", window.startDate);
  if (window.endDate) qs.set("endDate", window.endDate);
}

// ─── Types ────────────────────────────────────────────────────────────────────

/** Rows differ by mode: My-Brand returns SPEND/creatives, Competitors Days/Assets. */
export type OverviewRow = OverviewReportRow | OverviewCompetitorReportRow;

export type OverviewStatusData = {
  status: "ACCEPTED" | "PROCESSING" | "COMPLETED" | "FAILED";
  /** Present on FAILED — surfaced verbatim by the page. Written by `withErrorHandling`. */
  error?: string;
  message?: string;
  rows?: OverviewRow[];
  /**
   * Per-(topic, bucket) comparison-chart data — Competitors mode only. Asset % and the
   * winsorized-normalized Days/Assets are precomputed server-side (see the transform).
   */
  chartRows?: OverviewCompetitorChartRow[];
  /** Per-(topic, week, bucket) weekly asset counts — Competitors mode only. */
  weeklyRows?: OverviewTopicWeeklyRow[];
  /** Per-Scenario aggregated chart rows — My-Brand mode only. */
  myBrandChartRows?: OverviewChartRow[];
  /** Per-KPI topic series — My-Brand mode only (Info tab metric grid). */
  myBrandKpiSeries?: OverviewKpiSeries[];
  /** Distinct group-by dimension values — carried on COMPLETED for the
   *  OPTIONS_REQUESTED group-by-options job (additive; other pollers ignore it). */
  data?: string[] | TopicExampleRow[] | TopicFatiguePoint[];
};

/** Topic-detail loader result: pending (202 + correlationId) or ready (200 data). */
export type TopicDetailAsyncResult<T> =
  | { status: "pending"; correlationId: string }
  | { status: "ready"; data: T[] };

export type OverviewSummaryResult =
  | { status: "pending"; correlationId: string }
  | {
      status: "ready";
      rows: OverviewRow[];
      // chartRows and weeklyRows present in Competitors mode, absent in My-Brand.
      chartRows?: OverviewCompetitorChartRow[];
      weeklyRows?: OverviewTopicWeeklyRow[];
      // myBrandChartRows present in My-Brand mode, absent in Competitors.
      myBrandChartRows?: OverviewChartRow[];
      myBrandKpiSeries?: OverviewKpiSeries[];
    };

// ─── Keys ─────────────────────────────────────────────────────────────────────

export const overviewKeys = {
  all: ["overview"] as const,
  summaries: () => [...overviewKeys.all, "summary"] as const,
  summary: (tenantId: string, filters: OverviewFilters, globalFilters: MyBrandOverviewFilters) =>
    [...overviewKeys.summaries(), "my-brand", tenantId, filters, globalFilters] as const,
  competitorSummary: (tenantId: string, filters: OverviewFilters, competitorFilters: CompetitorOverviewFilters) =>
    [...overviewKeys.summaries(), "competitors", tenantId, filters, competitorFilters] as const,
  scenarioOptions: (tenantId: string, competitorFilters: CompetitorFiltersInput) =>
    [...overviewKeys.all, "scenario-options", tenantId, competitorFilters] as const,
  myBrandScenarioOptions: (tenantId: string) =>
    [...overviewKeys.all, "scenario-options", "my-brand", tenantId] as const,
  myBrandSubFeatureOptions: (tenantId: string) =>
    [...overviewKeys.all, "subfeature-options", "my-brand", tenantId] as const,
  subFeatureOptions: (tenantId: string, competitorFilters: CompetitorFiltersInput) =>
    [...overviewKeys.all, "subfeature-options", tenantId, competitorFilters] as const,
  groupByOptions: (tenantId: string, groupBy: string, window: OverviewDateWindow) =>
    [...overviewKeys.all, "group-by-options", tenantId, groupBy, window] as const,
  topicDetailExamples: (
    tenantId: string,
    filters: OverviewFilters,
    globalFilters: MyBrandOverviewFilters,
    context: TopicDetailContext,
  ) => [...overviewKeys.all, "topic-detail-examples", tenantId, filters, globalFilters, context] as const,
  topicDetailFatigue: (
    tenantId: string,
    filters: OverviewFilters,
    globalFilters: MyBrandOverviewFilters,
    context: TopicDetailContext,
    kpi: string,
  ) => [...overviewKeys.all, "topic-detail-fatigue", tenantId, filters, globalFilters, context, kpi] as const,
  compTopicDetailExamples: (
    tenantId: string,
    filters: OverviewFilters,
    competitorFilters: CompetitorOverviewFilters,
    context: CompTopicDetailContext,
  ) =>
    [...overviewKeys.all, "comp-topic-detail-examples", tenantId, filters, competitorFilters, context] as const,
  compTopicDetailFatigue: (
    tenantId: string,
    filters: OverviewFilters,
    competitorFilters: CompetitorOverviewFilters,
    context: CompTopicDetailContext,
  ) =>
    [...overviewKeys.all, "comp-topic-detail-fatigue", tenantId, filters, competitorFilters, context] as const,
  statuses: () => [...overviewKeys.all, "status"] as const,
  status: (correlationId: string) => [...overviewKeys.statuses(), correlationId] as const,
};

// ─── Constants ────────────────────────────────────────────────────────────────

export const POLL_INTERVAL_MS = 2000;
export const MAX_POLL_DURATION_MS = 5 * 60 * 1000;
/**
 * Examples batches re-run BigQuery on every scroll, so we poll more aggressively
 * than the 2s summary cadence to keep each page snappy.
 */
export const EXAMPLES_POLL_INTERVAL_MS = 750;

// ─── Topic-detail Examples: server-side batched loading ─────────────────────────

/** A single lazily-loaded batch of example rows (`EXAMPLES_PAGE_SIZE` + lookahead). */
export type ExamplesBatch<T> = T[];

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });
}

/**
 * Polls `/overview/status/:id` until the async batch job completes or times out,
 * returning the rows the worker delivered on the status payload. The first poll
 * fires immediately (no leading delay); `intervalMs` controls the cadence after.
 */
async function pollTopicDetailStatus<T>(
  correlationId: string,
  intervalMs: number,
  signal?: AbortSignal,
): Promise<T[]> {
  const startedAt = Date.now();
  for (;;) {
    const { data } = await authFetch<OverviewStatusData>(
      `/overview/status/${correlationId}`,
      { signal },
    );
    const status = data?.status;
    if (status === "COMPLETED") return (data?.data as T[] | undefined) ?? [];
    if (status === "FAILED") {
      throw new ApiError(500, data?.error ?? data?.message ?? "Topic detail request failed", "JOB_FAILED");
    }
    if (Date.now() - startedAt > MAX_POLL_DURATION_MS) {
      throw new ApiError(504, "Topic detail request timed out", "POLL_TIMEOUT");
    }
    await sleep(intervalMs, signal);
  }
}

/**
 * Fetches one Examples batch. Every request runs a fresh BigQuery `LIMIT/OFFSET`
 * query (no result caching), so the API always returns `202` + `correlationId`
 * and we poll `/status` for the batch rows delivered on completion. A `200` with
 * inline data is still honored defensively. Each page is one real BQ round-trip.
 */
async function fetchExamplesBatch<T>(url: string, signal?: AbortSignal): Promise<ExamplesBatch<T>> {
  const first = await authFetch<T[]>(url, { signal });
  if (first.data != null) return first.data;
  return pollTopicDetailStatus<T>(first.correlationId!, EXAMPLES_POLL_INTERVAL_MS, signal);
}

/**
 * Next `offset` when the last batch was full — a smaller batch means the last
 * page was reached. Paging also stops once the next offset would cross the
 * `EXAMPLES_MAX_ROWS` ceiling, bounding worst-case BigQuery cost per topic.
 */
function nextExamplesOffset(lastBatchLength: number, lastOffset: number): number | undefined {
  if (lastBatchLength < EXAMPLES_FETCH_SIZE) return undefined;
  const next = lastOffset + EXAMPLES_PAGE_SIZE;
  return next >= EXAMPLES_MAX_ROWS ? undefined : next;
}

/** Flattens loaded batches into a de-duplicated row list (drops the lookahead row). */
export function flattenExampleBatches<T>(pages: ReadonlyArray<ExamplesBatch<T>> | undefined): T[] {
  if (!pages) return [];
  return pages.flatMap((page) => page.slice(0, EXAMPLES_PAGE_SIZE));
}

// ─── Query options ────────────────────────────────────────────────────────────

/**
 * Builds the resolved querystring for the My-Brand `/overview/summary` request.
 * Extracted so the page can render the exact URL sent (debug section) without
 * duplicating the param logic.
 */
export function buildOverviewSummarySearch(
  filters: OverviewFilters,
  globalFilters: MyBrandOverviewFilters,
): string {
  const qs = new URLSearchParams({ dateRange: globalFilters.dateRange });
  if (globalFilters.platforms.length > 0) qs.set("platforms", globalFilters.platforms.join(","));
  if (globalFilters.accounts.length > 0) qs.set("accounts", globalFilters.accounts.join(","));
  if (globalFilters.products.length > 0) qs.set("products", globalFilters.products.join(","));

  if (filters.groupBy) qs.set("groupBy", filters.groupBy);
  if (filters.groupByValues.length > 0) qs.set("groupByValues", filters.groupByValues.join(","));
  if (filters.subFeature.length > 0) qs.set("subFeature", filters.subFeature.join(","));
  if (filters.featureTimeFrame.length > 0) qs.set("featureTimeFrame", filters.featureTimeFrame.join(","));
  if (filters.objectType.length > 0) qs.set("objectType", filters.objectType.join(","));
  if (filters.effectiveStatus.length > 0) qs.set("effectiveStatus", filters.effectiveStatus.join(","));
  if (filters.scenario.length > 0) qs.set("scenario", filters.scenario.join(","));
  if (filters.audienceSegment.length > 0) qs.set("audienceSegment", filters.audienceSegment.join(","));
  if (filters.advancedFilters.length > 0) qs.set("advancedFilters", JSON.stringify(filters.advancedFilters));
  appendDateWindow(qs, filters);
  appendDateWindow(qs, globalFilters);

  return qs.toString();
}

function buildTopicDetailQuerySearch(
  filters: OverviewFilters,
  globalFilters: MyBrandOverviewFilters,
  context: TopicDetailContext,
  extra?: Record<string, string>,
): string {
  const qs = new URLSearchParams(buildOverviewSummarySearch(filters, globalFilters));
  qs.set("search", context.search);
  qs.set("segment1", context.segment1);
  qs.set("segment2", context.segment2);
  qs.set("segment3", context.segment3);
  if (context.topicTitle) qs.set("topicTitle", context.topicTitle);
  if (extra) {
    for (const [key, value] of Object.entries(extra)) {
      qs.set(key, value);
    }
  }
  return qs.toString();
}

/** Querystring for `GET /overview/topic-detail/examples` (debug + query options). */
export function buildTopicDetailExamplesSearch(
  filters: OverviewFilters,
  globalFilters: MyBrandOverviewFilters,
  context: TopicDetailContext,
): string {
  return buildTopicDetailQuerySearch(filters, globalFilters, context);
}

/** Querystring for `GET /overview/topic-detail/fatigue` (debug + query options). */
export function buildTopicDetailFatigueSearch(
  filters: OverviewFilters,
  globalFilters: MyBrandOverviewFilters,
  context: TopicDetailContext,
  kpi: string,
): string {
  return buildTopicDetailQuerySearch(filters, globalFilters, context, { kpi, splitSeries: "1" });
}

const EMPTY_TOPIC_DETAIL_CONTEXT: TopicDetailContext = {
  topicTitle: "",
  topicDescription: "",
  scenario: "",
  featureTimeFrame: "",
  search: "All",
  segment1: "Hash",
  segment2: "All",
  segment3: "All",
};

/** Infinite (server-batched) loader for the My-Brand Examples tab. */
export function topicDetailExamplesInfiniteQueryOptions(
  tenantId: string | null,
  filters: OverviewFilters,
  globalFilters: MyBrandOverviewFilters,
  context: TopicDetailContext | null,
) {
  const search = context
    ? buildTopicDetailExamplesSearch(filters, globalFilters, context)
    : "";

  return infiniteQueryOptions({
    queryKey: overviewKeys.topicDetailExamples(
      tenantId ?? "",
      filters,
      globalFilters,
      context ?? EMPTY_TOPIC_DETAIL_CONTEXT,
    ),
    queryFn: ({ pageParam, signal }) =>
      fetchExamplesBatch<TopicExampleRow>(
        `/overview/topic-detail/examples?${search}&offset=${pageParam}`,
        signal,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastOffset) =>
      nextExamplesOffset(lastPage.length, lastOffset),
    enabled: !!tenantId && !!context,
    staleTime: Infinity,
    // Discard loaded pages when the drawer unmounts so reopening re-hits BigQuery.
    gcTime: 0,
    retry: false,
    refetchOnReconnect: false,
  });
}

export function topicDetailFatigueQueryOptions(
  tenantId: string | null,
  filters: OverviewFilters,
  globalFilters: MyBrandOverviewFilters,
  context: TopicDetailContext | null,
  kpi: string,
) {
  const search = context
    ? buildTopicDetailFatigueSearch(filters, globalFilters, context, kpi)
    : "";

  return queryOptions<TopicDetailAsyncResult<TopicFatiguePoint> | null>({
    queryKey: overviewKeys.topicDetailFatigue(
      tenantId ?? "",
      filters,
      globalFilters,
      context ?? {
        topicTitle: "",
        topicDescription: "",
        scenario: "",
        featureTimeFrame: "",
        search: "All",
        segment1: "Hash",
        segment2: "All",
        segment3: "All",
      },
      kpi,
    ),
    queryFn: async ({ signal }) => {
      const { data, correlationId } = await authFetch<TopicFatiguePoint[]>(
        `/overview/topic-detail/fatigue?${search}`,
        { signal },
      );
      if (data == null) return { status: "pending", correlationId: correlationId! };
      return { status: "ready", data };
    },
    enabled: !!tenantId && !!context && !!kpi,
    staleTime: Infinity,
    // Discard on unmount so each drawer open re-runs BigQuery (no result caching).
    gcTime: 0,
    retry: false,
    refetchOnReconnect: false,
  });
}

function buildCompetitorTopicDetailBaseSearch(
  filters: OverviewFilters,
  competitorFilters: CompetitorOverviewFilters,
): URLSearchParams {
  const qs = new URLSearchParams({ mode: "competitors", dateRange: competitorFilters.dateRange });
  if (competitorFilters.myIndustry) qs.set("myIndustry", competitorFilters.myIndustry);
  if (competitorFilters.myBrand) qs.set("myBrand", competitorFilters.myBrand);
  if (competitorFilters.industryProducts.length > 0) {
    qs.set("industryProducts", competitorFilters.industryProducts.join(","));
  }
  if (competitorFilters.competitorsIndustry) {
    qs.set("competitorsIndustry", competitorFilters.competitorsIndustry);
  }
  if (competitorFilters.mainCompetitor) qs.set("mainCompetitor", competitorFilters.mainCompetitor);
  if (competitorFilters.benchmark.length > 0) qs.set("benchmark", competitorFilters.benchmark.join(","));
  qs.set("platforms", competitorFilters.platforms.join(","));

  if (filters.groupBy) qs.set("groupBy", filters.groupBy);
  if (filters.groupByValues.length > 0) qs.set("groupByValues", filters.groupByValues.join(","));
  if (filters.subFeature.length > 0) qs.set("subFeature", filters.subFeature.join(","));
  if (filters.featureTimeFrame.length > 0) qs.set("featureTimeFrame", filters.featureTimeFrame.join(","));
  if (filters.objectType.length > 0) qs.set("objectType", filters.objectType.join(","));
  if (filters.effectiveStatus.length > 0) qs.set("effectiveStatus", filters.effectiveStatus.join(","));
  if (filters.scenario.length > 0) qs.set("scenario", filters.scenario.join(","));
  if (filters.advancedFilters.length > 0) {
    qs.set("advancedFilters", JSON.stringify(filters.advancedFilters));
  }
  appendDateWindow(qs, filters);
  appendDateWindow(qs, competitorFilters);

  return qs;
}

function buildCompTopicDetailQuerySearch(
  filters: OverviewFilters,
  competitorFilters: CompetitorOverviewFilters,
  context: CompTopicDetailContext,
  extra?: Record<string, string>,
): string {
  const qs = buildCompetitorTopicDetailBaseSearch(filters, competitorFilters);
  qs.set("search", context.search);
  qs.set("segment1", context.segment1);
  qs.set("segment2", context.segment2);
  qs.set("segment3", context.segment3);
  qs.set("competitorBqFilter", context.competitorBqFilter);
  qs.set("bucket", context.bucket);
  if (context.topicTitle) qs.set("topicTitle", context.topicTitle);
  if (extra) {
    for (const [key, value] of Object.entries(extra)) {
      qs.set(key, value);
    }
  }
  return qs.toString();
}

/** Querystring for `GET /overview/topic-detail/comp/examples`. */
export function buildCompTopicDetailExamplesSearch(
  filters: OverviewFilters,
  competitorFilters: CompetitorOverviewFilters,
  context: CompTopicDetailContext,
): string {
  return buildCompTopicDetailQuerySearch(filters, competitorFilters, context);
}

const EMPTY_COMP_TOPIC_DETAIL_CONTEXT: CompTopicDetailContext = {
  topicTitle: "",
  topicDescription: "",
  bucket: "you",
  entityDisplayLabel: "",
  featureTimeFrame: "",
  scenario: "",
  search: "All",
  segment1: "Hash",
  segment2: "All",
  segment3: "All",
  competitorBqFilter: "",
  chartMetrics: { Days: 0, Assets: 0, assetPct: 0, daysAssets: null },
};

/** Infinite (server-batched) loader for the Competitors Examples tab. */
export function compTopicDetailExamplesInfiniteQueryOptions(
  tenantId: string | null,
  filters: OverviewFilters,
  competitorFilters: CompetitorOverviewFilters,
  context: CompTopicDetailContext | null,
) {
  const search = context
    ? buildCompTopicDetailExamplesSearch(filters, competitorFilters, context)
    : "";

  return infiniteQueryOptions({
    queryKey: overviewKeys.compTopicDetailExamples(
      tenantId ?? "",
      filters,
      competitorFilters,
      context ?? EMPTY_COMP_TOPIC_DETAIL_CONTEXT,
    ),
    queryFn: ({ pageParam, signal }) =>
      fetchExamplesBatch<CompTopicExampleRow>(
        `/overview/topic-detail/comp/examples?${search}&offset=${pageParam}`,
        signal,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastOffset) =>
      nextExamplesOffset(lastPage.length, lastOffset),
    enabled: !!tenantId && !!context,
    staleTime: Infinity,
    // Discard loaded pages when the drawer unmounts so reopening re-hits BigQuery.
    gcTime: 0,
    retry: false,
    refetchOnReconnect: false,
  });
}

/** Querystring for `GET /overview/topic-detail/comp/fatigue` (KPI is client-side only). */
export function buildCompTopicDetailFatigueSearch(
  filters: OverviewFilters,
  competitorFilters: CompetitorOverviewFilters,
  context: CompTopicDetailContext,
): string {
  return buildCompTopicDetailExamplesSearch(filters, competitorFilters, context);
}

export function compTopicDetailFatigueQueryOptions(
  tenantId: string | null,
  filters: OverviewFilters,
  competitorFilters: CompetitorOverviewFilters,
  context: CompTopicDetailContext | null,
) {
  const search = context
    ? buildCompTopicDetailFatigueSearch(filters, competitorFilters, context)
    : "";

  return queryOptions<TopicDetailAsyncResult<CompTopicFatiguePoint> | null>({
    queryKey: overviewKeys.compTopicDetailFatigue(
      tenantId ?? "",
      filters,
      competitorFilters,
      context ?? EMPTY_COMP_TOPIC_DETAIL_CONTEXT,
    ),
    queryFn: async ({ signal }) => {
      const { data, correlationId } = await authFetch<CompTopicFatiguePoint[]>(
        `/overview/topic-detail/comp/fatigue?${search}`,
        { signal },
      );
      if (data == null) return { status: "pending", correlationId: correlationId! };
      return { status: "ready", data };
    },
    enabled: !!tenantId && !!context,
    staleTime: Infinity,
    // Discard on unmount so each drawer open re-runs BigQuery (no result caching).
    gcTime: 0,
    retry: false,
    refetchOnReconnect: false,
  });
}

export function overviewSummaryQueryOptions(
  tenantId: string | null,
  filters: OverviewFilters,
  globalFilters: MyBrandOverviewFilters,
) {
  const search = buildOverviewSummarySearch(filters, globalFilters);

  return queryOptions<OverviewSummaryResult | null>({
    queryKey: overviewKeys.summary(tenantId ?? "", filters, globalFilters),
    queryFn: async ({ signal }) => {
      const { data, correlationId } = await authFetch<{
        rows: OverviewRow[];
        myBrandChartRows?: OverviewChartRow[];
        myBrandKpiSeries?: OverviewKpiSeries[];
      }>(
        `/overview/summary?${search}`,
        { signal },
      );
      if (!data) return { status: "pending", correlationId: correlationId! };
      return {
        status: "ready",
        rows: data.rows ?? [],
        myBrandChartRows: data.myBrandChartRows ?? [],
        myBrandKpiSeries: data.myBrandKpiSeries ?? [],
      };
    },
    enabled: !!tenantId,
    staleTime: Infinity,
    retry: false,
    refetchOnReconnect: false,
  });
}

export function buildOverviewCompetitorSummarySearch(
  filters: OverviewFilters,
  competitorFilters: CompetitorOverviewFilters,
): string {
  const qs = new URLSearchParams({ mode: "competitors", dateRange: competitorFilters.dateRange });
  if (competitorFilters.myIndustry) qs.set("myIndustry", competitorFilters.myIndustry);
  if (competitorFilters.myBrand) qs.set("myBrand", competitorFilters.myBrand);
  if (competitorFilters.industryProducts.length > 0) qs.set("industryProducts", competitorFilters.industryProducts.join(","));
  if (competitorFilters.competitorsIndustry) qs.set("competitorsIndustry", competitorFilters.competitorsIndustry);
  if (competitorFilters.mainCompetitor) qs.set("mainCompetitor", competitorFilters.mainCompetitor);
  if (competitorFilters.benchmark.length > 0) qs.set("benchmark", competitorFilters.benchmark.join(","));
  // Always send platforms (even empty) — a missing param defaults to ['meta'] server-side;
  // an empty string is how the UI expresses "all platforms". Mirrors the dashboard factory.
  qs.set("platforms", competitorFilters.platforms.join(","));

  // Overview-specific BQ filters (groupBy is unused in the Competitors bar but harmless).
  if (filters.groupBy) qs.set("groupBy", filters.groupBy);
  if (filters.subFeature.length > 0) qs.set("subFeature", filters.subFeature.join(","));
  if (filters.featureTimeFrame.length > 0) qs.set("featureTimeFrame", filters.featureTimeFrame.join(","));
  if (filters.objectType.length > 0) qs.set("objectType", filters.objectType.join(","));
  if (filters.effectiveStatus.length > 0) qs.set("effectiveStatus", filters.effectiveStatus.join(","));
  if (filters.scenario.length > 0) qs.set("scenario", filters.scenario.join(","));
  if (filters.advancedFilters.length > 0) qs.set("advancedFilters", JSON.stringify(filters.advancedFilters));
  appendDateWindow(qs, filters);
  appendDateWindow(qs, competitorFilters);

  return qs.toString();
}

export function overviewCompetitorSummaryQueryOptions(
  tenantId: string | null,
  filters: OverviewFilters,
  competitorFilters: CompetitorOverviewFilters,
) {
  const qs = new URLSearchParams(buildOverviewCompetitorSummarySearch(filters, competitorFilters));

  return queryOptions<OverviewSummaryResult | null>({
    queryKey: overviewKeys.competitorSummary(tenantId ?? "", filters, competitorFilters),
    queryFn: async ({ signal }) => {
      const { data, correlationId } = await authFetch<{
        rows: OverviewRow[];
        chartRows?: OverviewCompetitorChartRow[];
        weeklyRows?: OverviewTopicWeeklyRow[];
      }>(
        `/overview/summary?${qs.toString()}`,
        { signal },
      );
      if (!data) return { status: "pending", correlationId: correlationId! };
      return { status: "ready", rows: data.rows ?? [], chartRows: data.chartRows ?? [], weeklyRows: data.weeklyRows ?? [] };
    },
    enabled: !!tenantId,
    staleTime: Infinity,
    retry: false,
    refetchOnReconnect: false,
  });
}

/**
 * Distinct Scenario filter options (Competitors mode), sourced from the
 * features_mapping table and scoped to the selected entity context. Synchronous
 * cached endpoint — returns the option list directly.
 */
export function overviewScenarioOptionsQueryOptions(
  tenantId: string | null,
  competitorFilters: CompetitorFiltersInput,
) {
  const qs = new URLSearchParams();
  if (competitorFilters.myIndustry) qs.set("myIndustry", competitorFilters.myIndustry);
  if (competitorFilters.competitorsIndustry) qs.set("competitorsIndustry", competitorFilters.competitorsIndustry);
  if (competitorFilters.mainCompetitor) qs.set("mainCompetitor", competitorFilters.mainCompetitor);
  if (competitorFilters.myBrand) qs.set("myBrand", competitorFilters.myBrand);
  if (competitorFilters.benchmark.length > 0) qs.set("benchmark", competitorFilters.benchmark.join(","));

  return queryOptions<string[]>({
    queryKey: overviewKeys.scenarioOptions(tenantId ?? "", competitorFilters),
    queryFn: async ({ signal }) => {
      const { data } = await authFetch<string[]>(
        `/overview/scenario-options?${qs.toString()}`,
        { signal },
      );
      return data ?? [];
    },
    enabled: !!tenantId,
    staleTime: Infinity,
    retry: false,
    refetchOnReconnect: false,
  });
}

/**
 * Distinct Scenario filter options for My-Brand mode. Reuses the same
 * `/overview/scenario-options` endpoint with `mode=my-brand`: the handler sources
 * options from the core dataset (`platform='core'`), scoped to the tenant's
 * `customerPartition` (falling back to `defaultBrand`) BQ client name(s) — i.e. the
 * exact `Scenario` values present in `core.full_reco_table` for this tenant, which
 * is what the My-Brand summary query filters on. My-Brand global filters
 * (platforms/accounts/products/dateRange) don't map to the source table, so they
 * are intentionally not sent.
 */
export function overviewMyBrandScenarioOptionsQueryOptions(tenantId: string | null) {
  return queryOptions<string[]>({
    queryKey: overviewKeys.myBrandScenarioOptions(tenantId ?? ""),
    queryFn: async ({ signal }) => {
      const { data } = await authFetch<string[]>("/overview/scenario-options?mode=my-brand", { signal });
      return data ?? [];
    },
    enabled: !!tenantId,
    staleTime: Infinity,
    retry: false,
    refetchOnReconnect: false,
  });
}

/**
 * My-Brand Sub-categories (sub-feature) options, mapped per Scenario. Sourced from
 * `features_mapping` core rows scoped to the tenant's `customerPartition` (→ `defaultBrand`),
 * so the options reflect the sub-features that actually exist for each scenario in this
 * tenant's `core.full_reco_table`. The component unions the entries for the selected
 * scenario(s) via `subFeatureOptionsForScenarios`. `staleTime: Infinity` mirrors the
 * scenario-options factory — a `features_mapping` sync landing mid-session surfaces on reload.
 */
export function overviewMyBrandSubFeatureOptionsQueryOptions(tenantId: string | null) {
  return queryOptions<Record<string, string[]>>({
    queryKey: overviewKeys.myBrandSubFeatureOptions(tenantId ?? ""),
    queryFn: async ({ signal }) => {
      const { data } = await authFetch<Record<string, string[]>>("/overview/subfeature-options?mode=my-brand", { signal });
      return data ?? {};
    },
    enabled: !!tenantId,
    staleTime: Infinity,
    retry: false,
    refetchOnReconnect: false,
  });
}

/**
 * Competitors Sub-categories (sub-feature) options, mapped per Scenario. Sourced from
 * `features_mapping` industry/competitor rows scoped to the selected entity context.
 * The component unions the entries for the selected scenario(s) via
 * `subFeatureOptionsForScenarios`.
 */
export function overviewSubFeatureOptionsQueryOptions(
  tenantId: string | null,
  competitorFilters: CompetitorFiltersInput,
) {
  const qs = new URLSearchParams();
  if (competitorFilters.myIndustry) qs.set("myIndustry", competitorFilters.myIndustry);
  if (competitorFilters.competitorsIndustry) qs.set("competitorsIndustry", competitorFilters.competitorsIndustry);
  if (competitorFilters.mainCompetitor) qs.set("mainCompetitor", competitorFilters.mainCompetitor);
  if (competitorFilters.myBrand) qs.set("myBrand", competitorFilters.myBrand);
  if (competitorFilters.benchmark.length > 0) qs.set("benchmark", competitorFilters.benchmark.join(","));

  return queryOptions<Record<string, string[]>>({
    queryKey: overviewKeys.subFeatureOptions(tenantId ?? "", competitorFilters),
    queryFn: async ({ signal }) => {
      const { data } = await authFetch<Record<string, string[]>>(
        `/overview/subfeature-options?${qs.toString()}`,
        { signal },
      );
      return data ?? {};
    },
    enabled: !!tenantId,
    staleTime: Infinity,
    retry: false,
    refetchOnReconnect: false,
  });
}

/**
 * Distinct values of the selected Group By dimension (My-Brand mode), for the dynamic
 * sub-filter. Sourced from `core.raw_table_view` (BQ DISTINCT) scoped to the tenant's
 * BQ client + date window, so the values match the buckets the chart groups on. Disabled
 * until a dimension is chosen; `Date` is excluded (unbounded time buckets → no sub-filter).
 * `staleTime: Infinity` — the server caches for 30m; within a session the list is stable.
 */
export function overviewGroupByOptionsQueryOptions(
  tenantId: string | null,
  groupBy: string,
  dateRange: string,
  window: OverviewDateWindow,
) {
  const qs = new URLSearchParams({ groupBy, dateRange });
  appendDateWindow(qs, window);
  return queryOptions<string[]>({
    queryKey: overviewKeys.groupByOptions(tenantId ?? "", groupBy, window),
    // Thin gateway: a warm cache-hit / empty / non-filterable dimension returns
    // `200 { data: [...] }` (present even when empty); a cold miss returns
    // `202 { correlationId }` and the helper polls /status to a terminal state.
    queryFn: ({ signal }) =>
      fetchAmberOptions<string[]>(
        `/overview/group-by-options?${qs.toString()}`,
        "/overview",
        { signal },
      ),
    enabled: !!tenantId && !!groupBy && groupBy !== "Date",
    staleTime: Infinity,
    retry: false,
    refetchOnReconnect: false,
  });
}

export function overviewStatusQueryOptions(
  correlationId: string | null,
  pollStartedAt: number,
) {
  return queryOptions<OverviewStatusData | null>({
    queryKey: overviewKeys.status(correlationId ?? ""),
    queryFn: async ({ signal }) => {
      const { data } = await authFetch<OverviewStatusData>(
        `/overview/status/${correlationId}`,
        { signal },
      );
      return data;
    },
    enabled: !!correlationId && pollStartedAt > 0,
    staleTime: Infinity,
    retry: false,
    refetchInterval: (query) => {
      if (!pollStartedAt || !correlationId) return false;
      const s = query.state.data?.status;
      if (s === "COMPLETED" || s === "FAILED") return false;
      if (Date.now() - pollStartedAt > MAX_POLL_DURATION_MS) return false;
      return POLL_INTERVAL_MS;
    },
    refetchOnReconnect: false,
  });
}
